import { DuplicateCourse, GetCourses } from '@/connection/courses';
import { useState } from 'react';
import { ICourse } from '@/types/courses';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import { useAppSelector } from '@/store/hooks';
import { getUser } from '@/store/slices/user/selectors';
import { getWorkingProject } from '@/store/slices/project/selectors';
import toastTrigger from '@ui/toast/toast-trigger';
import Popover from '@ui/popover/popover';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import RotatableIcon from '@ui/RotatableIcon/rotatable-icon';
import AddFromLibraryModal from './modals/course-add-from-library-modal';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import getThemeColorsCssMixVariable from '@/utils/colors/get-theme-color-css-mix-variable';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import { EToastTypes } from '@ui/toast/toast.types';

type NoCourseProps = {
  setCourse: React.Dispatch<React.SetStateAction<ICourse | null>>;
  courseCanBeAddedToEvent: () => boolean;
  initializeCourse: () => Promise<void>;
  working: boolean;
};

export default function NoCourse({ setCourse, courseCanBeAddedToEvent, initializeCourse, working }: NoCourseProps) {
  const user = useAppSelector(getUser);
  const workingEvent = useAppSelector(getWorkingProject);

  const [showAddFromLibraryModal, setShowAddFromLibraryModal] = useState(false);
  const [selectedLibraryCourse, setSelectedLibraryCourse] = useState<ICourse | null>(null);
  const [loadingLibrary, setLoadingLibrary] = useState(true);
  const [libraryCourses, setLibraryCourses] = useState<ICourse[]>([]);

  const loadAllCourses = async () => {
    if (!user) return;

    try {
      const courses = await GetCourses(user.active_channel);
      setLibraryCourses(courses);
    } catch {
      toastTrigger('Error fetching courses library', {
        type: EToastTypes.error,
        autoClose: 5000,
      });
    }

    setLoadingLibrary(false);
  };

  const handleInitializeCourse = async () => {
    await initializeCourse();
    setShowAddFromLibraryModal(false);
  };

  const addCourseFromLibrary = async () => {
    if (!user?.active_channel || !workingEvent?.event || !selectedLibraryCourse) return;

    try {
      const duplicatedCourse = await DuplicateCourse(
        user?.active_channel,
        workingEvent?.event,
        selectedLibraryCourse?.uuid
      );
      setCourse(duplicatedCourse);
      setShowAddFromLibraryModal(false);

      if (workingEvent?.sessions.length > 1) {
        // Requirements are tied to a specific session, so if existing event has multiple session the cloned requirements
        // will just be all assigned to the first session and the admin will need to update that since we can't
        // magically do it for them
        toastTrigger('Your event has multiple sessions please update your requirements accordingly', {
          type: EToastTypes.warning,
          autoClose: 50000,
        });
      }
    } catch {
      toastTrigger('Error adding course from library', {
        type: EToastTypes.error,
        autoClose: 5000,
      });
    }
  };

  const handleAddCourseFromLibraryButton = () => {
    if (!courseCanBeAddedToEvent()) {
      return;
    }
    loadAllCourses();
    setShowAddFromLibraryModal(true);
  };

  return (
    <>
      <Container className="flex-column ai-center jc-center gap-20" data-testid="course-create">
        <Text className="H3" tag="h3">
          No course added
        </Text>
        <Popover
          closeOnClick
          backgroundColor="var(--gray-700)"
          cycleWithArrowKeys
          contentClassName="margin-t-2"
          renderTriggerButton={({ open, closing }) => (
            <Button
              backgroundColor={getThemeColorCSSVariable()}
              hoverBackgroundColor={getThemeColorsCssMixVariable()}
              designSize="medium"
              className="flex ai-center gap-6"
            >
              <Text className="L1" color={getThemeColorContrastCSSVariable()}>
                Configure Course
              </Text>
              <RotatableIcon name={ICONS.KEYBOARD_ARROW_DOWN} size={16} rotate={open && !closing ? '180deg' : '0'} />
            </Button>
          )}
        >
          <Button
            hoverBackgroundColor="var(--gray-600)"
            outlineOnFocus={false}
            className="radius-0 text-left padding-v-10 padding-h-16 width-100-percent flex ai-center gap-10"
            onClick={handleInitializeCourse}
            loading={working}
          >
            <Icon name={ICONS.ADD} size={12}></Icon>
            <Text color="var(--gray-100)" className="P2">
              New Course
            </Text>
          </Button>
          <Button
            hoverBackgroundColor="var(--gray-600)"
            outlineOnFocus={false}
            className="radius-0 text-left padding-v-10 padding-h-16 width-100-percent flex ai-center gap-10"
            onClick={handleAddCourseFromLibraryButton}
            loading={working}
          >
            <Icon name={ICONS.FOLDER} size={12}></Icon>
            <Text color="var(--gray-100)" className="P2">
              From Library
            </Text>
          </Button>
        </Popover>
      </Container>
      <AddFromLibraryModal
        open={showAddFromLibraryModal}
        onClose={() => setShowAddFromLibraryModal(false)}
        selectedCourse={selectedLibraryCourse}
        onSelectCourse={setSelectedLibraryCourse}
        loadingLibrary={loadingLibrary}
        libraryCourses={libraryCourses}
        onAddFromLibrary={addCourseFromLibrary}
        onCreateNew={handleInitializeCourse}
      />
    </>
  );
}
